import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { User } from "../types/user";
// auth characteristics of current user relevant to client
interface AuthState {
    user: User | null;
    role: string | null;
    // whether the user has completed onboarding
    isOnboarded: boolean;
    // whether the user is in admin view / has admin rights
    isAdminView: boolean;
    // whether the user is authenticated by login service
    isAuthenticated: boolean;
    loading: boolean;
    error: string | null;
    //whether the user has verified their email
    emailVerified: boolean;
    // Whether the user has realtor access
    mlsApproved: boolean;
    // Whether the user status is active
    isActive: boolean;
    // Whether the user subscription status is active
    isSubscribed: boolean;
    // Whether the site is in maintenance mode
    maintenance: boolean;
    // Maintenance message to display
    maintenanceMessage: string | null;
    // Whether the user has connected social accounts
    socialsConnected: boolean;
    // Whether the user is an admin
    isAdmin: boolean;
    // Whether the user created their first listing
    listingCreated: boolean;
    // Impersonation state
    isImpersonating: boolean;
    impersonatedUser: User | null;
    originalUser: User | null;
    wasAdminView: boolean;
    impersonationToken: string | null;
    sessionId: string | null;
    impersonationExpired: boolean;
}

const initialState: AuthState = {
    user: null,
    role: null,
    isOnboarded: false,
    isAdminView: false,
    isAuthenticated: false,
    loading: true,
    error: null,
    emailVerified: false,
    mlsApproved: false,
    isActive: false,
    isSubscribed: false,
    maintenance: false,
    maintenanceMessage: null,
    socialsConnected: false,
    isAdmin: false,
    listingCreated: false,
    isImpersonating: false,
    impersonatedUser: null,
    originalUser: null,
    wasAdminView: false,
    impersonationToken: null,
    sessionId: null,
    impersonationExpired: false,
};

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        setUser(state, action: PayloadAction<User | null>) {
            state.user = action.payload;
            state.isAuthenticated = !!action.payload;
            state.loading = false;
            state.mlsApproved = action.payload?.mlsApproved || false;
        },
        setRole(state, action: PayloadAction<string | null>) {
            state.role = action.payload;
        },
        setIsOnboarded(state, action: PayloadAction<boolean>) {
            state.isOnboarded = action.payload;
        },
        setIsAdminView(state, action: PayloadAction<boolean>) {
            state.isAdminView = action.payload;
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        },
        setError(state, action: PayloadAction<string | null>) {
            state.error = action.payload;
            state.loading = false;
        },
        setAdmin(state, action: PayloadAction<boolean>) {
            state.isAdmin = action.payload;
        },
        setEmailVerified(state, action: PayloadAction<boolean>) {
            state.emailVerified = action.payload;
        },
        setIsActive(state, action: PayloadAction<boolean>) {
            state.isActive = action.payload;
        },
        setIsSubscribed(state, action: PayloadAction<boolean>) {
            state.isSubscribed = action.payload;
        },
        setListingCreated(state, action: PayloadAction<boolean>) {
            state.listingCreated = action.payload;
        },
        logout(state) {
            state.user = null;
            state.role = null;
            state.isAuthenticated = false;
            state.isAdminView = false;
            state.isOnboarded = false;
            state.loading = false;
            sessionStorage.removeItem("isAdminView");
        },
        setMaintenance(state, action: PayloadAction<boolean>) {
            state.maintenance = action.payload;
        },
        setMaintenanceMessage(state, action: PayloadAction<string | null>) {
            state.maintenanceMessage = action.payload;
        },
        setSocialsConnected(state, action: PayloadAction<boolean>) {
            state.socialsConnected = action.payload;
        },
        setImpersonationExpired(state, action: PayloadAction<boolean>) {
            state.impersonationExpired = action.payload;
        },
        startImpersonation(state, action: PayloadAction<{ user: User; impersonationToken: string; sessionId: string }>) {
            // Store original user and admin view state before impersonating
            if (!state.isImpersonating) {
                state.originalUser = state.user;
                state.wasAdminView = state.isAdminView;
            }
            state.impersonatedUser = action.payload.user;
            state.originalUser = state.user;
            state.user = action.payload.user;
            state.isImpersonating = true;
            state.impersonationToken = action.payload.impersonationToken;
            state.sessionId = action.payload.sessionId;
            // Update mlsApproved to reflect impersonated user's value
            state.mlsApproved = action.payload.user?.mlsApproved || false;
            // Store in sessionStorage
            sessionStorage.setItem("isImpersonating", "true");
            sessionStorage.setItem("impersonatedUser", JSON.stringify(action.payload.user));
            sessionStorage.setItem("wasAdminView", state.wasAdminView.toString());
            sessionStorage.setItem("impersonationToken", action.payload.impersonationToken);
            sessionStorage.setItem("sessionId", action.payload.sessionId);
            if (state.originalUser) {
                sessionStorage.setItem("originalUser", JSON.stringify(state.originalUser));
            }
        },
        stopImpersonation(state) {
            // Restore original user and admin view
            if (state.originalUser) {
                state.user = state.originalUser;
                // Restore original user's mlsApproved value
                state.mlsApproved = state.originalUser.mlsApproved || false;
            }
            // Restore admin view if it was enabled before impersonation
            state.user = state.originalUser;
            state.isAdminView = state.wasAdminView;
            state.impersonatedUser = null;
            state.isImpersonating = false;
            state.originalUser = null;
            state.wasAdminView = false;
            state.impersonationToken = null;
            state.sessionId = null;
            state.impersonationExpired = false;
            // Clear from sessionStorage
            sessionStorage.removeItem("isImpersonating");
            sessionStorage.removeItem("impersonatedUser");
            sessionStorage.removeItem("originalUser");
            sessionStorage.removeItem("wasAdminView");
            sessionStorage.removeItem("impersonationToken");
            sessionStorage.removeItem("sessionId");
            // Restore isAdminView in sessionStorage
            if (state.isAdminView) {
                sessionStorage.setItem("isAdminView", "true");
            }
        },
    },
});

export const {
    setUser,
    setRole,
    setIsOnboarded,
    setIsAdminView,
    setLoading,
    setError,
    setEmailVerified,
    setIsActive,
    setIsSubscribed,
    setMaintenance,
    setMaintenanceMessage,
    logout,
    setSocialsConnected,
    setAdmin,
    setListingCreated,
    startImpersonation,
    stopImpersonation,
    setImpersonationExpired,
} = authSlice.actions;

export default authSlice.reducer;
